/**
 * companyFeatures.js — 회사 공통 기능 표 (2026-09-11 구조 개편 2단계 C2)
 *
 * **메뉴와 주소가 이 표 한 장에서 나온다.**
 *   - 사이드바·홈 런처 링크: companyMenuSet.js 가 이 표를 읽는다
 *   - 라우트(/admin/ext/:code/*)  : AppRoutes.jsx 가 이 표를 읽는다
 *
 * 전에는 같은 16개가 두 파일에 손으로 적혀 있었고 맞춰주는 장치가 없었다.
 * 그래서 2026-09-10 에 실제로 어긋나 있었다 — 정산 관리는 메뉴에는 모든 회사에 떴는데
 * 주소는 도트·엘엔제이 두 개만 있어서 그 외 회사(미팅링크 유기자차·테스트 회사 2개)는
 * **눌러도 홈으로 튕겼다.** 표 한 장으로 모아 그 어긋남을 구조적으로 없앤다.
 *
 * 한 줄 추가 = 메뉴와 주소가 동시에 생긴다. 화면(컴포넌트)은 AppRoutes.company.jsx 의
 * COMPANY_ELEMENTS 가 같은 key 로 들고 있고, 둘이 어긋나면 즉시 실패한다.
 *
 * show(ef, af) — 노출 조건. 폐지된 ExternalCompanyAccordion 의 매트릭스를 그대로 옮겼다.
 *   ef = enabled_features (회사 설정) / af = accessible_features (뷰어 권한, null = 운영자 전체 접근)
 *   ⚠ 항목마다 검사 방식이 원래 다르다(값이 참일 때만 / 명시적 false 만 차단 / 검사 없음).
 *     통일하면 회사에 따라 없던 차단이 생기므로 그대로 보존한다.
 *
 * generalCode — 자기 회사(own) 모드에서 이미 일반 메뉴로 뜨는 도메인. 중복을 숨긴다(구 dupGuard).
 * tabKey      — 옛 링크 /dashboard?tab={tabKey}-{회사코드} 의 앞부분. 없는 기능도 있다.
 * hqPath      — 본사(이 사이트의 내부 조직)에서 같은 일을 하는 주소. 본사는 문이 **하나여야** 한다.
 *   /admin/ext/{본사코드}/... 로 들어오면 여기로 돌려보낸다(CompanyRouteGuard).
 *   안 그러면 같은 회사의 데이터가 두 이름으로 갈린다 — 실제로 갈렸다(미팅링크 급여가
 *   본사 주소로는 'PINE', /admin/ext/LINK/payroll 로는 'LINK' 로 쌓였다).
 *   단말기 ID 관리는 본사 메뉴 자체가 없어 null 이다(돌려보낼 곳이 없다).
 *
 * 이름이 COMPANY_MENU_FEATURES 인 이유: tickettypemanagement/constants.js 에도
 * COMPANY_FEATURES 가 있다(회사 기능 토글 7종 — 여기 show() 가 읽는 바로 그 ef 키들).
 * 뜻이 다른 둘을 같은 이름으로 두면 잘못 가져다 쓰기 딱 좋다.
 */
import {
  User, Users, Clock, Calendar, Phone, TrendingUp, Banknote,
  DollarSign, Key, Gift, FileX, FileClock, Calculator, Award, FileText, Package, Map as MapIcon,
} from 'lucide-react';

export const COMPANY_MENU_FEATURES = [
  {
    key: 'users',
    hqPath: '/admin/users',
    tabKey: 'ext-users',
    group: '직원·근태',
    path: 'users',
    label: '직원 관리',
    Icon: User,
    desc: '직원 등록·수정·퇴직 처리를 관리합니다.',
    generalCode: 'USER_MANAGEMENT',
    show: (ef, af) => (!af || af.users !== false),
  },
  {
    key: 'attendance',
    hqPath: '/admin/attendance',
    tabKey: 'ext-attendance',
    group: '직원·근태',
    path: 'attendance',
    label: '출퇴근 관리',
    Icon: Clock,
    desc: '직원 출퇴근 현황과 기록을 관리합니다.',
    // 2026-09-29 출퇴근 달력을 이 메뉴의 [달력] 탭으로 합쳤다(안 A). 열쇠는 두 개 그대로 —
    //   메뉴는 둘 중 하나만 있어도 뜨고(달력 탭), 직원별 상세 탭은 출퇴근 관리 열쇠만(attendanceTabs.js).
    //   af.attendance_calendar 는 서버가 "달력 열쇠 또는 관리 열쇠" 로 준다. 옛 달력 주소는 AppRoutes 가 달력 탭으로 넘긴다.
    generalCode: 'ATTENDANCE_MANAGEMENT',
    // 자기 회사 메뉴 숨김 기준: 일반 '출퇴근 관리'가 이 회사 화면의 탭을 다 주는가.
    //   일반 관리 열쇠 = 두 탭 다 → 숨김 / 일반 달력 열쇠만 = 달력 탭뿐 → 회사 상세 열쇠(af.attendance)가 없을 때만 숨김.
    generalCovers: (permCodes, af) => permCodes.has('ATTENDANCE_MANAGEMENT')
      || (permCodes.has('ATTENDANCE_CALENDAR_MANAGEMENT') && !(af && af.attendance === true)),
    show: (ef, af) => (ef.attendance !== false)
      && (!af || af.attendance !== false || af.attendance_calendar === true),
  },
  {
    key: 'leave',
    hqPath: '/admin/leaves',
    tabKey: 'ext-leave',
    group: '직원·근태',
    path: 'leaves',
    label: '연차 관리',
    Icon: Calendar,
    desc: '직원 연차 부여·사용을 관리합니다.',
    generalCode: 'LEAVE_MANAGEMENT',
    show: (ef, af) => (ef.leave !== false) && (!af || af.leave !== false),
  },
  {
    key: 'leaveSettle',
    hqPath: '/admin/leave-settlement',
    group: '직원·근태',
    path: 'leave-settlement',
    label: '연차정산',
    Icon: Calculator,
    desc: '미사용 연차를 정산합니다.',
    show: (ef, af) => !af || af.leave_settlement === true,
  },
  {
    key: 'contractRenewal',
    hqPath: '/admin/contract-renewals',
    group: '직원·근태',
    path: 'contract-renewals',
    label: '계약갱신관리',
    Icon: FileClock,
    desc: '직원 계약 갱신 일정을 관리합니다.',
    show: (ef, af) => (ef.contract_renewal !== false) && (!af || af.contract_renewal !== false),
  },
  {
    key: 'payroll',
    hqPath: '/admin/payroll',
    group: '급여·보상',
    path: 'payroll',
    label: '급여 관리',
    Icon: Banknote,
    desc: '월별 급여 계산·확정을 관리합니다.',
    show: (ef, af) => !af || af.payroll === true,
  },
  {
    key: 'incentive',
    hqPath: '/admin/incentives',
    tabKey: 'ext-incentive',
    group: '급여·보상',
    path: 'incentives',
    label: '인센티브 관리',
    Icon: Gift,
    desc: '인센티브 기준·지급 내역을 관리합니다.',
    show: (ef, af) => ef.incentive === true && (!af || af.incentive === true),
  },
  {
    key: 'call',
    hqPath: '/admin/tm-calls',
    tabKey: 'ext-call-admin',
    group: '콜·영업',
    path: 'tm-calls',
    label: '일일콜현황 관리',
    Icon: Phone,
    desc: '일일 콜 기록을 관리합니다.',
    generalCode: 'TM_CALL_MANAGEMENT',
    show: (ef, af) => (ef.call !== false) && (!af || af.call !== false),
  },
  {
    // 회사별 미팅지도 관리 (2026-10-22). 사용자: "미팅지도 관리는 파인그룹, DOT, LNJ로 나누되 … 미팅지도 (개별)는 지금 처럼 유지".
    //   화면은 본사 미팅지도 관리 한 벌, 주소만 /api/admin/ext/{회사}/meeting-map. 열쇠 MEETING_MAP_{회사}_MANAGEMENT(회사 메뉴 규칙이 거른 것).
    key: 'meetingMap',
    hqPath: '/admin/meeting-map',
    group: '미팅',
    path: 'meeting-map',
    label: '미팅지도 관리',
    Icon: MapIcon,
    desc: '미팅 일정·결과를 지도로 관리합니다.',
    generalCode: 'MEETING_MAP_MANAGEMENT',
    show: (ef, af) => !af || af.meeting_map === true,
  },
  {
    key: 'scores',
    hqPath: '/admin/scores',
    group: '콜·영업',
    path: 'scores',
    label: '점수 관리',
    Icon: Award,
    desc: 'TM 월간 점수를 관리합니다.',
    show: (ef, af) => !af || af.tm_score === true,
  },
  {
    key: 'marketer1on1',
    hqPath: '/admin/marketer-consultant',
    group: '콜·영업',
    path: 'marketer-consultant',
    label: '마케터 1:1 관리',
    Icon: Users,
    desc: '마케터별 상담자 배분을 관리합니다.',
    show: (ef, af) => (ef.consultant_1on1 !== false) && (!af || af.consultant_1on1 === true),
  },
  {
    key: 'terminal',
    hqPath: null,
    tabKey: 'ext-terminal',
    group: '콜·영업',
    path: 'terminal-management',
    label: '단말기 ID 관리',
    Icon: Key,
    desc: '마케터 단말기 ID 를 관리합니다.',
    show: (ef, af) => (af ? af.terminal_management === true : ef.terminal_management === true),
  },
  {
    // 회사별 계약 관리 (2026-10-15, 4단계 계약 통합 4-5). 화면은 본사 계약 관리 한 벌, 주소만 /api/admin/ext/{회사}/contracts.
    //   열쇠 EXTERNAL_{회사}_CONTRACT_MANAGEMENT('계약 관리 (회사)') 가 **명시적으로 있을 때만** 보인다(fail-closed).
    //   자기 회사(own)에서 일반 '계약 관리'(CONTRACT_MANAGEMENT)가 있으면 중복이라 숨긴다.
    key: 'contract',
    hqPath: '/admin/contracts',
    group: '계약',
    path: 'contracts',
    label: '계약 관리',
    Icon: FileText,
    desc: '계약 등록·수정·현황을 관리합니다.',
    generalCode: 'CONTRACT_MANAGEMENT',
    show: (ef, af) => !af || af.contract === true,
  },
  {
    // 회사별 홍보물 관리 (2026-10-19, 5단계 홍보물 5-6). 화면은 본사 홍보물 관리 한 벌, 주소만 /api/admin/ext/{회사}/promotional.
    //   2026-10-19 회사 메뉴 규칙: 열쇠 EXTERNAL_{회사}_PROMOTIONAL_MANAGEMENT(회사가 '홍보물 주문 관리' 메뉴를 받았을 때만 효력)만 본다 —
    //   예전 '홍보물 켜짐'(enabled_features.promotional) 스위치는 안 쓴다. 묶음은 본사 '홍보물 관리'와 같은 '상품·설정'.
    key: 'promotional',
    hqPath: '/admin/promotional',
    group: '상품·설정',
    path: 'promotional',
    label: '홍보물 관리',
    Icon: Package,
    desc: '홍보물 주문을 관리합니다.',
    generalCode: 'PROMOTIONAL_MANAGEMENT',
    show: (ef, af) => !af || af.promotional === true,
  },
  {
    key: 'contractCancel',
    hqPath: '/admin/contract-cancellations',
    group: '계약',
    path: 'contract-cancellations',
    label: '계약취소 관리',
    Icon: FileX,
    desc: '계약 취소 요청·처리를 관리합니다.',
    show: (ef, af) => (ef.call !== false) && (!af || af.contract_cancel !== false),
  },
  {
    key: 'revenue',
    hqPath: '/admin/revenue',
    tabKey: 'ext-revenue',
    group: '정산·재무',
    path: 'revenue',
    label: '매출 관리',
    Icon: TrendingUp,
    desc: '매출 입력과 집계를 관리합니다.',
    show: (ef, af) => (ef.revenue !== false) && (!af || af.revenue !== false),
  },
  {
    key: 'settlement',
    hqPath: '/admin/settlement-management',
    tabKey: 'ext-settlement',
    group: '정산·재무',
    path: 'settlement',
    label: '정산서 관리',
    Icon: Banknote,
    desc: '마케터 정산서를 관리합니다.',
    show: (ef, af) => (ef.settlement !== false) && (!af || af.settlement !== false),
  },
  {
    key: 'payout',
    hqPath: '/admin/payout-management',
    tabKey: 'ext-payout',
    group: '정산·재무',
    path: 'payout',
    label: '정산 관리',
    Icon: DollarSign,
    desc: '정산 지급 내역을 관리합니다.',
    // 회사별 PAYOUT 권한, 또는 운영자(af=null)면 정산서 접근 조건과 같다.
    //   af 가 null 일 때 settlement 의 조건은 af 항이 전부 참이 되어 (ef.settlement !== false) 만 남는다.
    show: (ef, af) => (af ? af.payout === true : (ef.settlement !== false)),
  },
];

/** 옛 링크 /dashboard?tab=ext-*-{회사} 를 쓰는 기능들. Dashboard.jsx 가 읽는다. */
export const TAB_KEYED_FEATURES = COMPANY_MENU_FEATURES.filter((f) => f.tabKey);

/** 이 회사의 이 기능 주소. 코드는 원본 케이스를 유지한다(기존 아코디언과 동일). */
export function companyFeaturePath(code, feature) {
  return `/admin/ext/${code}/${feature.path}`;
}

/** 라우트용 상대 경로 (/admin 하위). */
export function companyFeatureRoutePath(feature) {
  return `ext/:code/${feature.path}`;
}

// 본사 판정 세 함수는 hqCompany.js 로 옮겼다(2026-10-19, 300줄 규칙) — 가져다 쓰는 곳이 바뀌지 않게 여기서 다시 내보낸다.
export { resolveHqCompanyCode, hqRedirectPath, hqCodeOrDefault } from './hqCompany';
